/**
 * Captura HD offscreen del viewport Cornerstone con VOI (WW/WL) activo.
 * Mosaico multi-viewport: canvas Cornerstone visible (WW/zoom/pan) sin overlays HTML/PHI.
 */

import cornerstone from 'cornerstone-core'

/** Lado máximo del JPEG enviado a Gemini (control de payload / latencia). */
export const COPILOT_MAX_SIDE = 1280
export const COPILOT_JPEG_QUALITY = 0.82
/** Gap entre tiles del mosaico (px). */
export const COPILOT_MOSAIC_GAP = 4
/**
 * UDV layout index = row * 4 + col (ver App.buildLayoutGrid).
 * @type {number}
 */
export const COPILOT_LAYOUT_STRIDE = 4

/**
 * @param {number} w
 * @param {number} h
 * @param {number} [maxSide]
 * @returns {{ width: number, height: number }}
 */
export function fitCaptureSize(w, h, maxSide = COPILOT_MAX_SIDE) {
  const width = Math.max(1, Math.floor(Number(w) || 1))
  const height = Math.max(1, Math.floor(Number(h) || 1))
  const max = Math.max(64, Math.floor(Number(maxSide) || COPILOT_MAX_SIDE))
  const longest = Math.max(width, height)
  if (longest <= max) return { width, height }
  const scale = max / longest
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}

/**
 * @param {number[]|null|undefined} layout - [rows, cols]
 * @returns {{ rows: number, cols: number }}
 */
export function normalizeLayoutDims(layout) {
  const rows = Math.max(
    1,
    Math.min(3, Math.floor(Number(layout && layout[0]) || 1))
  )
  const cols = Math.max(
    1,
    Math.min(3, Math.floor(Number(layout && layout[1]) || 1))
  )
  return { rows, cols }
}

/**
 * Índice de slot UDV (row-major con stride 4).
 * @param {number} row
 * @param {number} col
 * @param {number} [stride]
 */
export function layoutSlotIndex(row, col, stride = COPILOT_LAYOUT_STRIDE) {
  return Math.floor(Number(row) || 0) * stride + Math.floor(Number(col) || 0)
}

/**
 * @param {*} image
 * @param {*} viewport
 * @returns {{ windowWidth: number, windowCenter: number }|null}
 */
export function resolveVoiFromViewport(image, viewport) {
  const voi = viewport && viewport.voi
  if (
    voi &&
    Number.isFinite(Number(voi.windowWidth)) &&
    Number.isFinite(Number(voi.windowCenter)) &&
    Number(voi.windowWidth) > 0
  ) {
    return {
      windowWidth: Number(voi.windowWidth),
      windowCenter: Number(voi.windowCenter),
    }
  }
  if (!image) return null
  const ww = Number(image.windowWidth)
  const wc = Number(image.windowCenter)
  if (Number.isFinite(ww) && Number.isFinite(wc) && ww > 0) {
    return { windowWidth: ww, windowCenter: wc }
  }
  return null
}

/**
 * Renderiza imagen a canvas full-matrix con VOI clínico.
 * @param {*} image
 * @param {{ windowWidth?: number, windowCenter?: number }|null} voi
 * @param {{ width: number, height: number }} size
 * @returns {HTMLCanvasElement}
 */
export function renderImageToOffscreenCanvas(image, voi, size) {
  if (!image) throw new Error('no_image')
  const canvas = document.createElement('canvas')
  canvas.width = size.width
  canvas.height = size.height

  const hasPixelData = typeof image.getPixelData === 'function'
  const isWebPreview = typeof image.getImage === 'function' && !hasPixelData

  if (isWebPreview) {
    const ctx = canvas.getContext('2d', { alpha: false })
    if (!ctx) throw new Error('no_2d_context')
    ctx.fillStyle = '#000'
    ctx.fillRect(0, 0, size.width, size.height)
    const htmlImg = image.getImage()
    if (!htmlImg) throw new Error('no_html_image')
    ctx.drawImage(htmlImg, 0, 0, size.width, size.height)
    return canvas
  }

  let viewport
  try {
    viewport =
      typeof cornerstone.getDefaultViewport === 'function'
        ? cornerstone.getDefaultViewport(canvas, image)
        : {
            scale: 1,
            translation: { x: 0, y: 0 },
            voi: { windowWidth: 400, windowCenter: 40 },
            invert: false,
            pixelReplication: false,
            rotation: 0,
            hflip: false,
            vflip: false,
          }
  } catch (e) {
    viewport = {
      scale: 1,
      translation: { x: 0, y: 0 },
      voi: { windowWidth: 400, windowCenter: 40 },
      invert: false,
      pixelReplication: false,
      rotation: 0,
      hflip: false,
      vflip: false,
    }
  }

  viewport.scale =
    size.width / Math.max(1, image.width || image.columns || size.width)
  viewport.translation = { x: 0, y: 0 }
  if (voi) {
    viewport.voi = {
      windowWidth: voi.windowWidth,
      windowCenter: voi.windowCenter,
    }
  }

  const enabledElement = {
    element: canvas,
    canvas,
    image,
    invalid: true,
    needsRedraw: true,
    viewport,
  }

  const rendering = cornerstone.rendering || {}
  const renderFn = image.color
    ? rendering.renderColorImage || rendering.renderWebImage
    : rendering.renderGrayscaleImage || rendering.renderWebImage

  if (typeof renderFn === 'function') {
    renderFn(enabledElement, true)
    return canvas
  }

  if (typeof cornerstone.renderToCanvas === 'function') {
    // Nunca renderizar matriz nativa completa (mamo 3k–5k congela 10–20 s).
    const srcW = image.width || image.columns || size.width
    const srcH = image.height || image.rows || size.height
    const native = document.createElement('canvas')
    const capped = fitCaptureSize(
      srcW,
      srcH,
      Math.max(size.width, size.height)
    )
    native.width = capped.width
    native.height = capped.height
    try {
      cornerstone.renderToCanvas(native, image)
    } catch (e) {
      throw new Error('no_renderer')
    }
    if (native.width === size.width && native.height === size.height) {
      return native
    }
    const ctx = canvas.getContext('2d', { alpha: false })
    if (ctx) {
      ctx.fillStyle = '#000'
      ctx.fillRect(0, 0, size.width, size.height)
      ctx.drawImage(native, 0, 0, size.width, size.height)
    }
    return canvas
  }

  throw new Error('no_renderer')
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {number} [quality]
 * @returns {string} base64 sin prefijo data:
 */
export function canvasToJpegBase64(canvas, quality = COPILOT_JPEG_QUALITY) {
  const dataUrl = canvas.toDataURL('image/jpeg', quality)
  const idx = dataUrl.indexOf(',')
  if (idx < 0) throw new Error('bad_data_url')
  return dataUrl.slice(idx + 1)
}

/**
 * Obtiene JPEG HD del enabledElement activo.
 * @param {Object} enabledElement - cornerstone enabled element
 * @param {{ maxSide?: number, quality?: number }} [opts]
 * @returns {string} Base64 JPEG sin prefijo
 */
export function getHDImageBase64(enabledElement, opts = {}) {
  if (!enabledElement || !enabledElement.image) {
    throw new Error('no_enabled_element')
  }
  const image = enabledElement.image
  const rawW = image.width || image.columns || 0
  const rawH = image.height || image.rows || 0
  if (!rawW || !rawH) throw new Error('bad_image_size')

  const size = fitCaptureSize(rawW, rawH, opts.maxSide)
  const voi = resolveVoiFromViewport(image, enabledElement.viewport)
  const canvas = renderImageToOffscreenCanvas(image, voi, size)
  return canvasToJpegBase64(canvas, opts.quality)
}

/**
 * Resuelve enabledElement desde un DicomViewer (o DOM element habilitado).
 * @param {{ dicomImage?: HTMLElement }|HTMLElement|null} viewerOrElement
 * @returns {Object|null}
 */
export function getEnabledElementFromViewer(viewerOrElement) {
  if (!viewerOrElement) return null
  const el =
    viewerOrElement.dicomImage ||
    (viewerOrElement.nodeType === 1 ? viewerOrElement : null)
  if (!el) return null
  try {
    return cornerstone.getEnabledElement(el)
  } catch (e) {
    return null
  }
}

/**
 * Canvas Cornerstone del viewer (incluye WW/zoom/pan; sin overlays HTML).
 * @param {{ dicomImage?: HTMLElement }|null} viewer
 * @returns {HTMLCanvasElement|null}
 */
export function getCornerstoneCanvasFromViewer(viewer) {
  if (!viewer) return null
  const el = viewer.dicomImage
  if (!el || typeof el.querySelector !== 'function') return null
  const canvas =
    el.querySelector('canvas.cornerstone-canvas') || el.querySelector('canvas')
  if (!canvas || !canvas.width || !canvas.height) return null
  return canvas
}

/**
 * Copia un canvas fuente a uno nuevo.
 * @param {HTMLCanvasElement} source
 * @returns {HTMLCanvasElement|null}
 */
export function cloneCanvasPixels(source) {
  if (!source || !source.width || !source.height) return null
  const out = document.createElement('canvas')
  out.width = source.width
  out.height = source.height
  const ctx = out.getContext('2d', { alpha: false })
  if (!ctx) return null
  ctx.fillStyle = '#000'
  ctx.fillRect(0, 0, out.width, out.height)
  try {
    ctx.drawImage(source, 0, 0)
  } catch (e) {
    return null
  }
  return out
}

/**
 * Tile listo para mosaico: canvas Cornerstone o fallback offscreen HD.
 * @param {*} viewer
 * @param {{ maxSide?: number }} [opts]
 * @returns {HTMLCanvasElement|null}
 */
export function captureViewerTileCanvas(viewer, opts = {}) {
  if (!viewer || !viewer.image) return null
  const live = getCornerstoneCanvasFromViewer(viewer)
  if (live) {
    const cloned = cloneCanvasPixels(live)
    if (cloned) return cloned
  }
  const enabled = getEnabledElementFromViewer(viewer)
  if (!enabled || !enabled.image) return null
  try {
    const image = enabled.image
    const rawW = image.width || image.columns || 0
    const rawH = image.height || image.rows || 0
    if (!rawW || !rawH) return null
    const tileMax = Math.max(
      64,
      Math.floor((opts.maxSide || COPILOT_MAX_SIDE) / 2)
    )
    const size = fitCaptureSize(rawW, rawH, tileMax)
    const voi = resolveVoiFromViewport(image, enabled.viewport)
    return renderImageToOffscreenCanvas(image, voi, size)
  } catch (e) {
    return null
  }
}

/**
 * @param {Array<*>|null|undefined} viewers - dicomViewersRefs
 * @param {number[]} [layout] - [rows, cols]
 * @param {{ maxSide?: number }} [opts]
 * @returns {{ row: number, col: number, index: number, canvas: HTMLCanvasElement }[]}
 */
export function collectLayoutTiles(viewers, layout, opts = {}) {
  const { rows, cols } = normalizeLayoutDims(layout)
  const refs = Array.isArray(viewers) ? viewers : []
  const tiles = []
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const index = layoutSlotIndex(r, c)
      const viewer = refs[index]
      const canvas = captureViewerTileCanvas(viewer, opts)
      if (!canvas) continue
      tiles.push({ row: r, col: c, index, canvas })
    }
  }
  return tiles
}

/**
 * Compone mosaico respetando posiciones del layout (celdas vacías = negro).
 * @param {{ row: number, col: number, canvas: HTMLCanvasElement }[]} tiles
 * @param {{ rows: number, cols: number }} dims
 * @param {{ maxSide?: number, gap?: number }} [opts]
 * @returns {HTMLCanvasElement}
 */
export function composeViewportMosaic(tiles, dims, opts = {}) {
  const rows = Math.max(1, dims.rows || 1)
  const cols = Math.max(1, dims.cols || 1)
  const gap = Math.max(
    0,
    Math.floor(Number(opts.gap != null ? opts.gap : COPILOT_MOSAIC_GAP))
  )
  const list = Array.isArray(tiles) ? tiles : []
  if (!list.length) throw new Error('no_tiles')

  if (list.length === 1 && rows === 1 && cols === 1) {
    return list[0].canvas
  }

  let maxTileW = 1
  let maxTileH = 1
  for (let i = 0; i < list.length; i++) {
    const cv = list[i].canvas
    if (cv.width > maxTileW) maxTileW = cv.width
    if (cv.height > maxTileH) maxTileH = cv.height
  }

  const mosaicW = cols * maxTileW + (cols - 1) * gap
  const mosaicH = rows * maxTileH + (rows - 1) * gap
  const fitted = fitCaptureSize(
    mosaicW,
    mosaicH,
    opts.maxSide || COPILOT_MAX_SIDE
  )
  const scaleX = fitted.width / mosaicW
  const scaleY = fitted.height / mosaicH

  const out = document.createElement('canvas')
  out.width = fitted.width
  out.height = fitted.height
  const ctx = out.getContext('2d', { alpha: false })
  if (!ctx) throw new Error('no_2d_context')
  ctx.fillStyle = '#000'
  ctx.fillRect(0, 0, out.width, out.height)

  const byKey = {}
  for (let i = 0; i < list.length; i++) {
    const t = list[i]
    byKey[`${t.row},${t.col}`] = t.canvas
  }

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const src = byKey[`${r},${c}`]
      if (!src) continue
      const dx = Math.round(c * (maxTileW + gap) * scaleX)
      const dy = Math.round(r * (maxTileH + gap) * scaleY)
      const dw = Math.max(1, Math.round(maxTileW * scaleX))
      const dh = Math.max(1, Math.round(maxTileH * scaleY))
      const srcR = src.width / Math.max(1, src.height)
      const cellR = dw / dh
      let dw2 = dw
      let dh2 = dh
      let ox = 0
      let oy = 0
      if (srcR > cellR) {
        dh2 = Math.max(1, Math.round(dw / srcR))
        oy = Math.floor((dh - dh2) / 2)
      } else {
        dw2 = Math.max(1, Math.round(dh * srcR))
        ox = Math.floor((dw - dw2) / 2)
      }
      ctx.drawImage(src, dx + ox, dy + oy, dw2, dh2)
    }
  }
  return out
}

/**
 * Captura Copilot: mosaico de viewports abiertos (WW/zoom/pan, sin PHI overlay).
 * @param {{
 *   viewers?: Array<*>,
 *   layout?: number[],
 *   fallbackEnabled?: Object|null,
 *   maxSide?: number,
 *   quality?: number,
 *   gap?: number,
 * }} [opts]
 * @returns {{ base64: string, tileCount: number, rows: number, cols: number }}
 */
export function getCopilotCaptureBase64(opts = {}) {
  const dims = normalizeLayoutDims(opts.layout)
  const tiles = collectLayoutTiles(opts.viewers, opts.layout, {
    maxSide: opts.maxSide,
  })

  if (tiles.length >= 1) {
    const mosaic = composeViewportMosaic(tiles, dims, {
      maxSide: opts.maxSide,
      gap: opts.gap,
    })
    return {
      base64: canvasToJpegBase64(mosaic, opts.quality),
      tileCount: tiles.length,
      rows: dims.rows,
      cols: dims.cols,
    }
  }

  if (opts.fallbackEnabled) {
    return {
      base64: getHDImageBase64(opts.fallbackEnabled, {
        maxSide: opts.maxSide,
        quality: opts.quality,
      }),
      tileCount: 1,
      rows: 1,
      cols: 1,
    }
  }

  throw new Error('no_enabled_element')
}
